
import { useEffect, useRef } from 'react';
import { LANGUAGES } from './LanguageSelector';

export interface TranscriptEntry {
  id: string;
  timestamp: number;
  originalText: string;
  translatedText: string;
  sourceLang: string;
  targetLang: string;
}

interface TranscriptPanelProps {
  entries: TranscriptEntry[];
  isTranslating: boolean;
  onCopy: (text: string) => void;
  onClear: () => void;
  onExport: () => void;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

export default function TranscriptPanel({
  entries,
  isTranslating,
  onCopy,
  onClear,
  onExport,
}: TranscriptPanelProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries]);

  const getLangName = (code: string) =>
    LANGUAGES.find(l => l.code === code)?.name || code;

  const getLangFlag = (code: string) =>
    LANGUAGES.find(l => l.code === code)?.flag || '🌐';

  const fullOriginal = entries.map(e => e.originalText).join(' ');
  const fullTranslated = entries.map(e => e.translatedText).join(' ');

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-file-text-line text-teal-500 text-lg"></i>
          </div>
          <h3 className="font-bold text-gray-800 text-sm">Live Transcript</h3>
          {entries.length > 0 && (
            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full">
              {entries.length} segments
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {entries.length > 0 && (
            <>
              <button
                onClick={() => onCopy(fullOriginal + '\n\n--- Translation ---\n\n' + fullTranslated)}
                title="Copy all"
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
              >
                <i className="ri-clipboard-line text-base"></i>
              </button>
              <button
                onClick={onExport}
                title="Export transcript"
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-teal-600 transition-colors cursor-pointer"
              >
                <i className="ri-download-line text-base"></i>
              </button>
              <button
                onClick={onClear}
                title="Clear transcript"
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line text-base"></i>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Entries */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {entries.length === 0 && !isTranslating ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-2xl mb-4">
              <i className="ri-mic-2-line text-3xl text-teal-400"></i>
            </div>
            <p className="text-gray-500 font-medium text-sm">No transcript yet</p>
            <p className="text-gray-400 text-xs mt-1">Start recording to see live transcription & translation</p>
          </div>
        ) : (
          <>
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow"
              >
                {/* Timestamp bar */}
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-gray-400 text-xs"></i>
                    </div>
                    <span className="text-xs font-mono text-gray-400">{formatTime(entry.timestamp)}</span>
                  </div>
                  <button
                    onClick={() => onCopy(`${entry.originalText}\n${entry.translatedText}`)}
                    className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-200 text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-clipboard-line text-xs"></i>
                  </button>
                </div>

                {/* Original */}
                <div className="px-4 py-3 border-b border-gray-50">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-sm">{getLangFlag(entry.sourceLang)}</span>
                    <span className="text-xs font-semibold text-gray-500">{getLangName(entry.sourceLang)}</span>
                    <span className="text-xs text-gray-300 ml-1">Original</span>
                  </div>
                  <p className="text-sm text-gray-800 leading-relaxed">{entry.originalText}</p>
                </div>

                {/* Translation */}
                <div className="px-4 py-3 bg-teal-50/40">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-sm">{getLangFlag(entry.targetLang)}</span>
                    <span className="text-xs font-semibold text-teal-600">{getLangName(entry.targetLang)}</span>
                    <span className="text-xs text-teal-300 ml-1">Translation</span>
                  </div>
                  <p className="text-sm text-teal-800 leading-relaxed font-medium">{entry.translatedText}</p>
                </div>
              </div>
            ))}

            {/* Live typing indicator */}
            {isTranslating && (
              <div className="bg-white rounded-xl border border-teal-200 shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-xs font-semibold text-gray-500">Transcribing...</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Summary footer */}
      {entries.length > 0 && (
        <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{entries.reduce((acc, e) => acc + e.originalText.split(' ').length, 0)} words transcribed</span>
            <span>{entries.reduce((acc, e) => acc + e.translatedText.split(' ').length, 0)} words translated</span>
          </div>
        </div>
      )}
    </div>
  );
}
